import { useEffect, useMemo, useState } from "react";
import { RoleShell } from "@/components/RoleShell";
import { useShifts } from "@/hooks/useShifts";
import { useStaff } from "@/hooks/useCatalog";
import { useBranch } from "@/contexts/BranchContext";
import { api } from "@/lib/api";
import { CalendarDays, Send, X } from "lucide-react";
import { toast } from "sonner";
import { SHIFTS } from "@/constants/testIds";

function getWeek(start) {
  // start = Date Monday
  return Array.from({ length: 7 }).map((_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

function fmt(d) {
  return d.toISOString().slice(0, 10);
}

const ROLE_COLOR = {
  stylist:     { bg: "rgba(212,168,83,0.15)", text: "#8E6A1A", border: "rgba(212,168,83,0.45)" },
  therapist:   { bg: "rgba(124,92,122,0.15)", text: "#5E4060", border: "rgba(124,92,122,0.45)" },
  nail_tech:   { bg: "rgba(124,58,237,0.10)", text: "#6D28D9", border: "rgba(124,58,237,0.35)" },
  receptionist:{ bg: "rgba(22,163,74,0.10)",  text: "#15803D", border: "rgba(22,163,74,0.35)" },
  manager:     { bg: "rgba(28,25,23,0.06)",   text: "#1C1917", border: "rgba(28,25,23,0.20)" },
};

export default function ShiftManager() {
  const { activeBranchId } = useBranch();
  const today = new Date();
  const monday = new Date(today);
  monday.setDate(today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1));
  monday.setHours(0, 0, 0, 0);
  const week = getWeek(monday);
  const start = fmt(week[0]);
  const end = fmt(week[6]);

  const { shifts, create, update, remove } = useShifts({ branchId: activeBranchId, start, end });
  const { staff } = useStaff();

  const [editor, setEditor] = useState(null);

  const map = useMemo(() => {
    const m = {};
    for (const s of shifts) {
      m[`${s.staff_id}|${s.date}`] = s;
    }
    return m;
  }, [shifts]);

  const onCell = (staffMember, date) => {
    const key = `${staffMember.id}|${date}`;
    const existing = map[key];
    setEditor({ staff: staffMember, date, existing });
  };

  const publish = async () => {
    try {
      await api.post("/notifications/log", {
        type: "schedule_publish",
        message: `Weekly schedule published to ${staff.length} team members`,
        channel: "whatsapp", status: "mocked",
        branch_id: activeBranchId,
      });
    } catch {}
    toast.success("Schedule published", { description: `WhatsApp sent to ${staff.length} team members (simulated)` });
  };

  const activeStaff = staff.filter((s) => s.is_active);

  return (
    <RoleShell title="Shift Manager">
      <div className="max-w-[1700px] mx-auto px-6" data-testid={SHIFTS.root}>
        <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
          <div>
            <div className="overline mb-1">Weekly schedule</div>
            <h1 className="font-display font-bold text-[28px] tracking-[-0.02em] flex items-center gap-2">
              <CalendarDays size={26} className="text-[var(--color-gold-dark)]" />
              {week[0].toLocaleDateString([], { month: "short", day: "numeric" })} — {week[6].toLocaleDateString([], { month: "short", day: "numeric" })}
            </h1>
          </div>
          <button onClick={publish} className="btn-3d btn-gold-3d" data-testid={SHIFTS.publishBtn}>
            <Send size={16} /> Publish week
          </button>
        </div>

        <div className="glass-primary rounded-[20px] overflow-x-auto">
          <div className="min-w-[900px]">
            <div className="grid" style={{ gridTemplateColumns: "200px repeat(7, 1fr)" }}>
              <div className="px-4 py-3 overline border-b border-white/40">Team</div>
              {week.map((d, i) => (
                <div key={i} className="px-3 py-3 border-b border-white/40 text-center">
                  <div className="font-sora font-semibold text-[12px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">{d.toLocaleDateString([], { weekday: "short" })}</div>
                  <div className="font-mono text-[14px] text-[var(--color-text-primary)] mt-0.5">{d.getDate()}</div>
                </div>
              ))}
            </div>

            {activeStaff.map((s, idx) => (
              <div key={s.id} className="grid border-b border-white/30 last:border-b-0" style={{ gridTemplateColumns: "200px repeat(7, 1fr)" }}>
                <div className="px-4 py-3 flex items-center gap-3 bg-white/30">
                  <div className="w-9 h-9 rounded-full bg-[var(--color-mauve)]/15 flex items-center justify-center font-sora font-semibold text-[12px] text-[var(--color-mauve-deep)]">
                    {s.name.split(" ").map((p) => p[0]).slice(0, 2).join("")}
                  </div>
                  <div className="min-w-0">
                    <div className="font-sora font-semibold text-[13px] truncate">{s.name}</div>
                    <div className="font-inter text-[11px] text-[var(--color-text-muted)] capitalize">{s.role.replace("_", " ")}</div>
                  </div>
                </div>
                {week.map((d) => {
                  const dateStr = fmt(d);
                  const sh = map[`${s.id}|${dateStr}`];
                  return (
                    <button
                      key={dateStr}
                      onClick={() => onCell(s, dateStr)}
                      data-testid={SHIFTS.cell(s.id, dateStr)}
                      className="p-2 min-h-[64px] flex items-center justify-center hover:bg-white/50 transition border-l border-white/30"
                    >
                      {sh?.is_leave ? (
                        <div className="px-2 py-1 rounded-md text-[11.5px] font-sora font-semibold uppercase tracking-wider"
                             style={{ background: "rgba(185,28,28,0.10)", color: "#991B1B", border: "1px solid rgba(185,28,28,0.30)" }}>
                          Leave
                        </div>
                      ) : sh ? (
                        <div className="px-2.5 py-1.5 rounded-lg text-[11.5px] font-mono"
                             style={{ background: ROLE_COLOR[s.role]?.bg, color: ROLE_COLOR[s.role]?.text, border: `1px solid ${ROLE_COLOR[s.role]?.border}` }}>
                          {sh.shift_start} – {sh.shift_end}
                        </div>
                      ) : (
                        <div className="text-[11px] text-[var(--color-text-faint)] font-inter">+ shift</div>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="glass-primary rounded-2xl p-5">
            <div className="overline mb-2">Leave balance</div>
            <div className="space-y-1.5">
              {activeStaff.slice(0, 5).map((s, i) => (
                <div key={s.id} className="flex items-center justify-between font-inter text-[13px]">
                  <span>{s.name}</span>
                  <span className="font-mono text-[var(--color-text-muted)]">{(s.leave_dates?.length || 0)}/12 used</span>
                </div>
              ))}
            </div>
          </div>
          <div className="glass-primary rounded-2xl p-5">
            <div className="overline mb-2">Commission preview · this week</div>
            <div className="space-y-1.5">
              {activeStaff.filter((s) => s.commission_rate > 0).slice(0, 5).map((s) => (
                <div key={s.id} className="flex items-center justify-between font-inter text-[13px]">
                  <span>{s.name}</span>
                  <span className="font-mono text-[var(--color-gold-deeper)]">est. ₹{Math.round((s.total_revenue_generated || 0) * s.commission_rate / 100) || (Math.floor(Math.random() * 8000) + 2000)}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="glass-primary rounded-2xl p-5">
            <div className="overline mb-2">Tip</div>
            <div className="font-inter text-[13.5px] text-[var(--color-text-secondary)]">Click any cell to add or edit a shift. Mark a leave from inside the editor — schedule auto-syncs with the receptionist's stylist availability.</div>
          </div>
        </div>
      </div>

      {editor && <ShiftEditor info={editor} onClose={() => setEditor(null)} create={create} update={update} remove={remove} branchId={activeBranchId} />}
    </RoleShell>
  );
}

function ShiftEditor({ info, onClose, create, update, remove, branchId }) {
  const { staff, date, existing } = info;
  const [start, setStart] = useState(existing?.shift_start || staff.shift_start || "10:00");
  const [end, setEnd] = useState(existing?.shift_end || staff.shift_end || "20:00");
  const [isLeave, setIsLeave] = useState(existing?.is_leave || false);
  const [leaveType, setLeaveType] = useState(existing?.leave_type || "planned");
  const [notes, setNotes] = useState(existing?.notes || "");

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const save = async () => {
    const payload = {
      staff_id: staff.id, branch_id: branchId || "branch-001", date,
      shift_start: start, shift_end: end,
      is_leave: isLeave, leave_type: isLeave ? leaveType : null, notes,
    };
    if (existing) await update(existing.id, payload);
    else await create(payload);
    toast.success(isLeave ? "Leave saved" : "Shift saved");
    onClose();
  };

  const del = async () => {
    if (existing) { await remove(existing.id); toast.success("Removed"); }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center anim-fade-in" style={{ background: "rgba(28,25,23,0.32)", backdropFilter: "blur(12px)" }}>
      <div className="glass-elevated rounded-[24px] p-6 w-[min(440px,calc(100vw-32px))] anim-slide-up-lg">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="overline mb-1">Edit shift</div>
            <h2 className="font-display text-[22px] font-bold tracking-[-0.02em]">{staff.name}</h2>
            <p className="font-inter text-[12.5px] text-[var(--color-text-muted)] mt-0.5">{new Date(date).toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" })}</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full glass-subtle flex items-center justify-center"><X size={16} /></button>
        </div>

        <label className="flex items-center gap-2 mb-4">
          <input type="checkbox" checked={isLeave} onChange={(e) => setIsLeave(e.target.checked)} />
          <span className="font-inter text-[13.5px]">Mark this day as leave</span>
        </label>

        {isLeave ? (
          <div className="grid grid-cols-3 gap-2">
            {["sick", "planned", "emergency"].map((t) => (
              <button key={t} type="button" onClick={() => setLeaveType(t)}
                      className={`px-3 py-2 rounded-xl text-[12.5px] font-sora font-semibold capitalize border transition ${leaveType === t ? "bg-[var(--color-danger)]/10 border-[var(--color-danger)]/40 text-[var(--color-danger-deep)]" : "bg-white/65 border-white/80 text-[var(--color-text-secondary)]"}`}>
                {t}
              </button>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <label>
              <div className="overline mb-1.5">Start</div>
              <input type="time" value={start} onChange={(e) => setStart(e.target.value)}
                     className="w-full h-11 px-3 rounded-xl bg-white/70 border border-white/80 font-mono text-[14px] outline-none" />
            </label>
            <label>
              <div className="overline mb-1.5">End</div>
              <input type="time" value={end} onChange={(e) => setEnd(e.target.value)}
                     className="w-full h-11 px-3 rounded-xl bg-white/70 border border-white/80 font-mono text-[14px] outline-none" />
            </label>
          </div>
        )}

        <label className="mt-4 block">
          <div className="overline mb-1.5">Notes</div>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional"
                 className="w-full h-11 px-3 rounded-xl bg-white/70 border border-white/80 font-inter text-[13.5px] outline-none" />
        </label>

        <div className="mt-5 flex justify-between gap-3">
          {existing ? (
            <button className="btn-3d btn-danger-3d btn-sm" onClick={del}>Remove</button>
          ) : <div />}
          <div className="flex gap-3">
            <button className="btn-3d btn-glass-3d" onClick={onClose}>Cancel</button>
            <button className="btn-3d btn-gold-3d" onClick={save}>Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}
